import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import "./EditProfile.scss";
import { getCurrentUserId, getAccessToken } from "../../awsAuth";
import { useNavigate } from "react-router-dom";
import DynamicHeader from "../Header/DynamicHeader";
import Cropper from "react-easy-crop";
import Alerts from "../Alerts/Alerts";

const DEFAULT_AVATAR_URL =
  "https://cvanto-photos.s3.us-east-1.amazonaws.com/DefaultAvatar/avatar.png";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:5000"
    : "");

// профільні типи
const PROFILE_TYPES = [
  { value: "services", label: "Послуги" },
  { value: "products", label: "Товари" },
  { value: "handmade", label: "Ручна робота" },
  { value: "education", label: "Освіта" },
  { value: "influencer", label: "Інфлюенсер" },
  { value: "business", label: "Бізнес" },
];

const SERVICE_CATEGORIES = [
  { value: "Житло та нерухомість", label: "Житло та нерухомість" },
  { value: "Транспорт", label: "Транспорт" },
  { value: "Гаджети", label: "Гаджети" },
  { value: "Карʼєра", label: "Карʼєра" },
  { value: "Майстри та послуги", label: "Майстри та послуги" },
  { value: "Дім та затишок", label: "Дім та затишок" },
  { value: "Для саду та подвір'я", label: "Для саду та подвір'я" },
  { value: "Малеча", label: "Малеча" },
  { value: "Домашні улюбленці", label: "Домашні улюбленці" },
  { value: "Стиль", label: "Стиль" },
  { value: "Активність та спорт", label: "Активність та спорт" },
  { value: "Б’юті", label: "Б’юті" },
  { value: "Знання та Навчання", label: "Знання та Навчання" },
  { value: "Оренда та прокат", label: "Оренда та прокат" },
  { value: "Запчастини та аксесуари", label: "Запчастини та аксесуари" },
  { value: "Аксесуари", label: "Аксесуари" },
  { value: "Продукти та їжа", label: "Продукти та їжа" },
  { value: "Хобі", label: "Хобі" },
];

function calcCompletionLocal({
  avatarUrl,
  bio,
  gender,
  profileType,
  birthDate,
  location,
}) {
  let passed = 0;
  if (avatarUrl && avatarUrl !== DEFAULT_AVATAR_URL) passed++;
  if (bio && String(bio).trim().length > 0) passed++;
  if (gender && String(gender).trim().length > 0) passed++;
  const whitelist = new Set(PROFILE_TYPES.map((t) => t.value));
  if (profileType && whitelist.has(profileType)) passed++;
  if (birthDate && String(birthDate).trim().length > 0) passed++;
  if (location && String(location).trim().length > 0) passed++;
  return Math.round((passed / 6) * 100);
}

function normalizeUrl(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  return "https://" + s.replace(/^\/+/, "");
}

function isValidHttpUrl(u) {
  try {
    const x = new URL(normalizeUrl(u));
    return x.protocol === "http:" || x.protocol === "https:";
  } catch {
    return false;
  }
}

function normalizeTag(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  const collapsed = s.replace(/\s+/g, " ").slice(0, 32);
  return collapsed;
}

function isValidTag(s) {
  if (!s) return false;
  return /^[\p{L}\p{N}\s.'’\-]+$/u.test(s);
}

function createImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (err) => reject(err));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function scrollToField(ref) {
  if (!ref?.current) return;
  ref.current.scrollIntoView({
    behavior: "smooth",
    block: "center",
  });

  setTimeout(() => {
    try {
      ref.current.focus();
    } catch {}
  }, 250);
}

async function getCroppedBlob(imageSrc, cropPixels, outSize = 512) {
  const image = await createImage(imageSrc);

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas context error");

  canvas.width = outSize;
  canvas.height = outSize;

  const sx = clamp(Math.round(cropPixels.x), 0, image.width);
  const sy = clamp(Math.round(cropPixels.y), 0, image.height);
  const sw = clamp(Math.round(cropPixels.width), 1, image.width - sx);
  const sh = clamp(Math.round(cropPixels.height), 1, image.height - sy);

  ctx.drawImage(image, sx, sy, sw, sh, 0, 0, outSize, outSize);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.92);
  });
}

const EditProfile = () => {
  const [username, setUsername] = useState("");
  const [bio, setBio] = useState("");
  const [gender, setGender] = useState("");
  const [profileType, setProfileType] = useState("");
  const [avatar, setAvatar] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [previousAvatarUrl, setPreviousAvatarUrl] = useState(null);
  const [shouldDeleteAvatar, setShouldDeleteAvatar] = useState(false);
  const [birthDate, setBirthDate] = useState("");
  const [location, setLocation] = useState("");
  const [phone, setPhone] = useState("");
  const [showLocation, setShowLocation] = useState(false);

  const [links, setLinks] = useState([]);
  const [linkLabel, setLinkLabel] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const MAX_LINKS = 3;

  const [serviceCategories, setServiceCategories] = useState([]);
  const [serviceTags, setServiceTags] = useState([]);
  const [serviceTagDraft, setServiceTagDraft] = useState("");
  const MAX_SERVICE_CATEGORIES = 5;
  const MAX_SERVICE_TAGS = 12;

  const [specExpanded, setSpecExpanded] = useState(false);
  const [specRender, setSpecRender] = useState(false);

  const [profileCompletion, setProfileCompletion] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [isAvatarMenuOpen, setIsAvatarMenuOpen] = useState(false);
  const [isAvatarMenuClosing, setIsAvatarMenuClosing] = useState(false);

  const [isMobile, setIsMobile] = useState(false);

  const avatarMenuRef = useRef(null);
  const usernameRef = useRef(null);
  const phoneRef = useRef(null);
  const profileTypeRef = useRef(null);
  const navigate = useNavigate();

  const showSpecBlock = !!profileType;

  const [isAvatarCropOpen, setIsAvatarCropOpen] = useState(false);
  const [avatarCropSrc, setAvatarCropSrc] = useState(null);
  const [avatarCropFile, setAvatarCropFile] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isCropping, setIsCropping] = useState(false);
  const avatarCropSrcRef = useRef(null);

  const [initialSnapshot, setInitialSnapshot] = useState(null);
  const [showUnsavedAlert, setShowUnsavedAlert] = useState(false);

  const pendingLeaveRef = useRef(null);
  const pendingHrefRef = useRef(null);
  const pendingHistoryBackRef = useRef(false);

  const allowPopRef = useRef(false);
  const lastUrlRef = useRef(
    window.location.pathname + window.location.search + window.location.hash
  );
  const navHistory = window.history;

  const stableStringify = useCallback((obj) => {
    const seen = new WeakSet();
    const sorter = (value) => {
      if (value === null || typeof value !== "object") return value;
      if (seen.has(value)) return value;
      seen.add(value);

      if (Array.isArray(value)) return value.map(sorter);

      const keys = Object.keys(value).sort();
      const out = {};
      keys.forEach((k) => (out[k] = sorter(value[k])));
      return out;
    };

    try {
      return JSON.stringify(sorter(obj));
    } catch (e) {
      return "";
    }
  }, []);

  const buildSnapshot = useCallback(
    (override = {}) => {
      const safeLinks = (override.links ?? links ?? [])
        .map((x) => ({
          label: String(x?.label || "").trim().slice(0, 50),
          url: String(x?.url || "").trim(),
        }))
        .filter((x) => x.url)
        .slice(0, MAX_LINKS);

      const safeCats = (override.serviceCategories ?? serviceCategories ?? [])
        .map((x) => String(x || "").trim())
        .filter(Boolean)
        .slice(0, MAX_SERVICE_CATEGORIES);

      const safeTags = (override.serviceTags ?? serviceTags ?? [])
        .map((x) => normalizeTag(x))
        .filter(Boolean)
        .slice(0, MAX_SERVICE_TAGS);

      const snap = {
        username: String(override.username ?? username ?? "").toString(),
        bio: String(override.bio ?? bio ?? "").toString(),
        gender: String(override.gender ?? gender ?? "").toString(),
        profileType: String(override.profileType ?? profileType ?? "").toString(),
        birthDate: String(override.birthDate ?? birthDate ?? "").toString(),
        location: String(override.location ?? location ?? "").toString(),
        phone: String(override.phone ?? phone ?? "").toString(),
        showLocation: !!(override.showLocation ?? showLocation),

        // avatar state
        avatarUrl: String(
          override.avatarUrl ??
            avatarPreview ??
            previousAvatarUrl ??
            DEFAULT_AVATAR_URL
        ).toString(),
        previousAvatarUrl: String(
          override.previousAvatarUrl ?? previousAvatarUrl ?? DEFAULT_AVATAR_URL
        ).toString(),
        shouldDeleteAvatar: !!(override.shouldDeleteAvatar ?? shouldDeleteAvatar),
        hasNewAvatarFile: !!(override.hasNewAvatarFile ?? !!avatar),

        links: safeLinks,
        serviceCategories: safeCats,
        serviceTags: safeTags,
      };

      return snap;
    },
    [
      username,
      bio,
      gender,
      profileType,
      birthDate,
      location,
      phone,
      showLocation,
      avatarPreview,
      previousAvatarUrl,
      shouldDeleteAvatar,
      avatar,
      links,
      serviceCategories,
      serviceTags,
    ]
  );

  const buildSnapshotFromFetched = useCallback((data) => {
    const incomingLinks = Array.isArray(data?.links) ? data.links : [];
    const normalizedLinks = incomingLinks
      .map((x) => ({
        label: String(x?.label || "").trim().slice(0, 50),
        url: String(x?.url || "").trim(),
      }))
      .filter((x) => x.url)
      .slice(0, MAX_LINKS);

    const incomingCats = Array.isArray(data?.serviceCategories)
      ? data.serviceCategories
      : [];
    const incomingTags = Array.isArray(data?.serviceTags) ? data.serviceTags : [];

    const safeCats = incomingCats
      .map((x) => String(x || "").trim())
      .filter(Boolean)
      .slice(0, MAX_SERVICE_CATEGORIES);

    const safeTags = incomingTags
      .map((x) => normalizeTag(x))
      .filter(Boolean)
      .slice(0, MAX_SERVICE_TAGS);

    const serverAvatar = String(data?.avatar || DEFAULT_AVATAR_URL);

    return {
      username: String(data?.username || ""),
      bio: String(data?.bio || ""),
      gender: String(data?.gender || ""),
      profileType: String(data?.profileType || ""),
      birthDate: String(data?.birthDate || ""),
      location: String(data?.location || ""),
      phone: String(data?.phone || ""),
      showLocation: !!data?.showLocation,

      avatarUrl: serverAvatar || DEFAULT_AVATAR_URL,
      previousAvatarUrl: serverAvatar || DEFAULT_AVATAR_URL,
      shouldDeleteAvatar: false,
      hasNewAvatarFile: false,

      links: normalizedLinks,
      serviceCategories: safeCats,
      serviceTags: safeTags,
    };
  }, []);

  const currentSnapshot = useMemo(() => buildSnapshot(), [buildSnapshot]);

  const isDirty = useMemo(() => {
    if (!initialSnapshot) return false;

    const a = stableStringify(initialSnapshot);
    const b = stableStringify(currentSnapshot);
    return a !== b;
  }, [initialSnapshot, currentSnapshot, stableStringify]);

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    const onDocClickCapture = (e) => {
      if (!isDirty) return;
      if (e.defaultPrevented) return;
      if (e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const a = e.target?.closest?.("a[href]");
      if (!a) return;

      const href = a.getAttribute("href");
      if (!href) return;

      const target = a.getAttribute("target");
      if (target && target !== "_self") return;
      if (a.hasAttribute("download")) return;
      if (href.startsWith("mailto:") || href.startsWith("tel:")) return;

      if (href.startsWith("http://") || href.startsWith("https://")) {
        try {
          const u = new URL(href, window.location.origin);
          if (u.origin !== window.location.origin) return;
        } catch (err) {
          return;
        }
      }

      const resolved = new URL(href, window.location.origin);
      const to = resolved.pathname + resolved.search + resolved.hash;
      const from =
        window.location.pathname + window.location.search + window.location.hash;

      if (to === from) return;

      e.preventDefault();
      e.stopPropagation();

      pendingHrefRef.current = to;
      pendingLeaveRef.current = null;
      pendingHistoryBackRef.current = false;

      setShowUnsavedAlert(true);
    };

    document.addEventListener("click", onDocClickCapture, true);
    return () => document.removeEventListener("click", onDocClickCapture, true);
  }, [isDirty]);

  useEffect(() => {
    const onPopState = () => {
      const currentUrl =
        window.location.pathname + window.location.search + window.location.hash;

      if (allowPopRef.current) {
        allowPopRef.current = false;
        lastUrlRef.current = currentUrl;
        return;
      }

      if (!isDirty) {
        lastUrlRef.current = currentUrl;
        return;
      }

      try {
        navHistory.pushState(null, "", lastUrlRef.current);
      } catch (e) {}

      pendingHistoryBackRef.current = true;
      pendingLeaveRef.current = null;
      pendingHrefRef.current = null;

      setShowUnsavedAlert(true);
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [isDirty, navHistory]);

  const attemptLeave = useCallback(
    (leaveFn) => {
      if (!isDirty) {
        leaveFn();
        return;
      }
      pendingLeaveRef.current = leaveFn;
      pendingHrefRef.current = null;
      pendingHistoryBackRef.current = false;
      setShowUnsavedAlert(true);
    },
    [isDirty]
  );

  const clearLeaveContext = () => {
    pendingLeaveRef.current = null;
    pendingHrefRef.current = null;
    pendingHistoryBackRef.current = false;
  };

  const closeUnsavedAlert = () => {
    setShowUnsavedAlert(false);
    clearLeaveContext();
  };

  const proceedWithoutSaving = () => {
    setShowUnsavedAlert(false);

    if (pendingHrefRef.current) {
      const to = pendingHrefRef.current;
      clearLeaveContext();
      navigate(to);
      return;
    }

    if (pendingHistoryBackRef.current) {
      clearLeaveContext();
      allowPopRef.current = true;
      navHistory.back();
      return;
    }

    if (typeof pendingLeaveRef.current === "function") {
      const fn = pendingLeaveRef.current;
      clearLeaveContext();
      fn();
      return;
    }

    clearLeaveContext();
  };

  // ---- Load Profile ----
  useEffect(() => {
    const loadProfile = async () => {
      const userId = getCurrentUserId();
      if (!userId) {
        setError("Не вдалося отримати ID користувача");
        return;
      }

      try {
        const token = await getAccessToken?.().catch(() => null);
        const response = await fetch(
          `${API_BASE}/api/account/${encodeURIComponent(userId)}`,
          {
            headers: token
              ? {
                  Authorization: `Bearer ${token}`,
                }
              : {},
          }
        );
        const data = await response.json();
        if (!response.ok)
          throw new Error(data.error || "Помилка отримання профілю");

        setUsername(data.username || "");
        setBio(data.bio || "");
        setGender(data.gender || "");
        setProfileType(data.profileType || "");
        setBirthDate(data.birthDate || "");
        setLocation(data.location || "");
        setPhone(data.phone || "");
        setShowLocation(data.showLocation || false);

        const serverAvatar = data.avatar || DEFAULT_AVATAR_URL;
        setAvatarPreview(serverAvatar);
        setPreviousAvatarUrl(serverAvatar);

        const incomingLinks = Array.isArray(data.links) ? data.links : [];
        const normalized = incomingLinks
          .map((x) => ({
            label: String(x?.label || "").trim(),
            url: String(x?.url || "").trim(),
          }))
          .filter((x) => x.url);
        setLinks(normalized.slice(0, MAX_LINKS));

        const incomingCats = Array.isArray(data.serviceCategories)
          ? data.serviceCategories
          : [];
        const incomingTags = Array.isArray(data.serviceTags)
          ? data.serviceTags
          : [];
        setServiceCategories(
          incomingCats
            .map((x) => String(x || "").trim())
            .filter(Boolean)
            .slice(0, MAX_SERVICE_CATEGORIES)
        );
        setServiceTags(
          incomingTags
            .map((x) => normalizeTag(x))
            .filter(Boolean)
            .slice(0, MAX_SERVICE_TAGS)
        );

        const completionFromDb = Number(data.profileCompletion || 0);
        setProfileCompletion(
          Number.isFinite(completionFromDb) &&
            completionFromDb >= 0 &&
            completionFromDb <= 100
            ? completionFromDb
            : calcCompletionLocal({
                avatarUrl: serverAvatar,
                bio: data.bio,
                gender: data.gender,
                profileType: data.profileType,
                birthDate: data.birthDate,
                location: data.location,
              })
        );

        if (data.profileType) {
          setSpecRender(true);
          setTimeout(() => setSpecExpanded(true), 30);
        }

        const snap = buildSnapshotFromFetched(data);
        setInitialSnapshot(snap);

        lastUrlRef.current =
          window.location.pathname +
          window.location.search +
          window.location.hash;

        setShouldDeleteAvatar(false);
        setAvatar(null);
      } catch (err) {
        console.error("Помилка при запиті профілю:", err);
        setError("Не вдалося завантажити профіль");
      }
    };

    loadProfile();
  }, [buildSnapshotFromFetched]);

  useEffect(() => {
    if (showSpecBlock) {
      setSpecRender(true);
      const t = setTimeout(() => setSpecExpanded(true), 20);
      return () => clearTimeout(t);
    } else {
      setSpecExpanded(false);
      const t = setTimeout(() => setSpecRender(false), 220);
      return () => clearTimeout(t);
    }
  }, [showSpecBlock]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isAvatarMenuOpen) return;

    const handleClickOutside = (e) => {
      if (avatarMenuRef.current) {
        const menuEl = avatarMenuRef.current;
        const clickInsideMenu = menuEl.contains(e.target);
        const clickOnAvatar = !!e.target.closest(".edit-profile__avatar");

        if (!clickInsideMenu && !clickOnAvatar) {
          closeAvatarMenu();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isAvatarMenuOpen]);

  useEffect(() => {
    setProfileCompletion(
      calcCompletionLocal({
        avatarUrl: avatarPreview || DEFAULT_AVATAR_URL,
        bio,
        gender,
        profileType,
        birthDate,
        location,
      })
    );
  }, [avatarPreview, bio, gender, profileType, birthDate, location]);

  const openAvatarMenu = () => {
    setIsAvatarMenuClosing(false);
    setIsAvatarMenuOpen(true);
  };

  const closeAvatarMenu = () => {
    if (!isAvatarMenuOpen || isAvatarMenuClosing) return;
    setIsAvatarMenuClosing(true);
    setTimeout(() => {
      setIsAvatarMenuOpen(false);
      setIsAvatarMenuClosing(false);
    }, 170);
  };

  const handleAvatarClick = () => {
    if (isAvatarMenuOpen) {
      closeAvatarMenu();
    } else {
      openAvatarMenu();
    }
  };

  const openAvatarCrop = (file) => {
    try {
      setError("");
      setSuccessMessage("");
      if (!file) return;

      if (avatarCropSrcRef.current) {
        try {
          URL.revokeObjectURL(avatarCropSrcRef.current);
        } catch {}
        avatarCropSrcRef.current = null;
      }

      const src = URL.createObjectURL(file);
      avatarCropSrcRef.current = src;

      setAvatarCropFile(file);
      setAvatarCropSrc(src);

      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);

      setIsAvatarCropOpen(true);
    } catch (e) {
      console.error(e);
      setError("Не вдалося відкрити редактор аватарки");
    }
  };

  const closeAvatarCrop = () => {
    setIsAvatarCropOpen(false);
    setIsCropping(false);

    setAvatarCropFile(null);
    setAvatarCropSrc(null);
    setCroppedAreaPixels(null);

    if (avatarCropSrcRef.current) {
      try {
        URL.revokeObjectURL(avatarCropSrcRef.current);
      } catch {}
      avatarCropSrcRef.current = null;
    }
  };

  const onCropComplete = (_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  };

  const applyAvatarCrop = async () => {
    if (!avatarCropSrc || !croppedAreaPixels) {
      setError("Не вдалося обрізати фото. Спробуйте ще раз.");
      return;
    }

    setIsCropping(true);
    setError("");

    try {
      const blob = await getCroppedBlob(avatarCropSrc, croppedAreaPixels, 512);
      if (!blob) throw new Error("Crop blob error");

      const croppedFile = new File([blob], `avatar_${Date.now()}.jpg`, {
        type: "image/jpeg",
      });

      setAvatar(croppedFile);
      setShouldDeleteAvatar(false);

      const newPreviewUrl = URL.createObjectURL(croppedFile);
      setAvatarPreview(newPreviewUrl);

      closeAvatarCrop();
      closeAvatarMenu();
    } catch (e) {
      console.error(e);
      setError("Помилка при обрізці аватарки");
      setIsCropping(false);
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      openAvatarCrop(file);
      closeAvatarMenu();
      try {
        e.target.value = "";
      } catch {}
    }
  };

  const handleDeleteAvatar = () => {
    setAvatar(null);
    setAvatarPreview(DEFAULT_AVATAR_URL);
    setShouldDeleteAvatar(true);
    closeAvatarMenu();
    setSuccessMessage("Аватарка буде видалена після збереження");
  };

    const handleUsernameChange = (e) => {
    setUsername(e.target.value);
    setFieldErrors((prev) => ({ ...prev, username: false }));
  };

  const handleBioChange = (e) => setBio(e.target.value);
  const handleGenderChange = (e) => setGender(e.target.value);

    const handleProfileTypeChange = (e) => {
    const next = e.target.value;
    setProfileType(next);
    setFieldErrors((prev) => ({ ...prev, profileType: false }));
  };

  const addLink = () => {
    setError("");
    const label = String(linkLabel || "").trim().slice(0, 50);
    const urlRaw = String(linkUrl || "").trim();
    if (!urlRaw) {
      setError("Введіть URL посилання");
      return;
    }
    if (links.length >= MAX_LINKS) {
      setError(`Максимум ${MAX_LINKS} посилання(-ь)`);
      return;
    }
    if (!isValidHttpUrl(urlRaw)) {
      setError("Некоректне посилання");
      return;
    }
    const url = normalizeUrl(urlRaw);
    if (links.some((l) => l.url === url)) {
      setError("Таке посилання вже додано");
      return;
    }
    const entry = { label: label || new URL(url).hostname, url };
    setLinks((prev) => [...prev, entry]);
    setLinkLabel("");
    setLinkUrl("");
  };

  const removeLinkAt = (idx) => setLinks((prev) => prev.filter((_, i) => i !== idx));

  // categories
  const toggleServiceCategory = (catValue) => {
    setError("");
    const v = String(catValue || "").trim();
    if (!v) return;
    setServiceCategories((prev) => {
      const has = prev.includes(v);
      if (has) return prev.filter((x) => x !== v);
      if (prev.length >= MAX_SERVICE_CATEGORIES) {
        setError(`Максимум ${MAX_SERVICE_CATEGORIES} категорій спеціалізації`);
        return prev;
      }
      return [...prev, v];
    });
  };

  // tags
  const addServiceTag = () => {
    setError("");
    const v = normalizeTag(serviceTagDraft);
    if (!v) return;
    if (!isValidTag(v)) {
      setError("Некоректний тег");
      return;
    }
    setServiceTags((prev) => {
      const lower = v.toLowerCase();
      const exists = prev.some((x) => String(x).toLowerCase() === lower);
      if (exists) {
        setServiceTagDraft("");
        return prev;
      }
      if (prev.length >= MAX_SERVICE_TAGS) {
        setError(`Максимум ${MAX_SERVICE_TAGS} тегів`);
        return prev;
      }
      return [...prev, v];
    });
    setServiceTagDraft("");
  };

  const removeServiceTagAt = (idx) => {
    setServiceTags((prev) => prev.filter((_, i) => i !== idx));
  };

  const saveProfile = async () => {
  const userId = getCurrentUserId();
  if (!userId) {
    setError("Не вдалося отримати ID користувача. Спробуйте увійти знову.");
    return false;
  }

  const token = await getAccessToken?.().catch(() => null);
  if (!token) {
    setError("Сесія недійсна. Увійдіть ще раз.");
    return false;
  }

  setError("");
    setSuccessMessage("");
    setFieldErrors({});

    if (!username.trim()) {
      setError("Ім’я користувача не може бути порожнім");
      setFieldErrors({ username: true });
      scrollToField(usernameRef);
      return false;
    }

    if (!profileType.trim()) {
      setError("Оберіть тип акаунта");
      setFieldErrors({ profileType: true });
      scrollToField(profileTypeRef);
      return false;
    }

    const digitsOnly = phone.replace(/\D/g, "");

    if (!phone.trim()) {
      setError("Номер телефону обов'язковий");
      setFieldErrors({ phone: true });
      scrollToField(phoneRef);
      return false;
    }

    if (digitsOnly.length !== 10 && digitsOnly.length !== 12) {
      setError("Номер телефону має містити або 10 цифр (0681234567), або 12 цифр (+380681234567)");
      setFieldErrors({ phone: true });
      scrollToField(phoneRef);
      return false;
    }

    setIsUploading(true);

    let avatarUrl = avatarPreview || DEFAULT_AVATAR_URL;

    if (avatar) {
  const formData = new FormData();
  formData.append("avatar", avatar);

  try {
    const response = await fetch(`${API_BASE}/upload/avatar`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.error || "Помилка при завантаженні");
    avatarUrl = data.avatarUrl;
  } catch (err) {
    console.error(err);
    setError(err?.message || "Помилка при завантаженні аватарки");
    setIsUploading(false);
    return false;
  }
}

    if (shouldDeleteAvatar && previousAvatarUrl !== DEFAULT_AVATAR_URL) {
  try {
    await fetch(
      `${API_BASE}/api/account/${encodeURIComponent(userId)}/avatar`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    avatarUrl = DEFAULT_AVATAR_URL;
  } catch (err) {
    console.error("Не вдалося видалити стару аватарку:", err);
  }
}

    const allowed = new Set(PROFILE_TYPES.map((t) => t.value));
    const safeProfileType = allowed.has(profileType) ? profileType : "";

    const safeLinks = (Array.isArray(links) ? links : [])
      .map((x) => ({
        label: String(x?.label || "").trim().slice(0, 50),
        url: normalizeUrl(String(x?.url || "").trim()),
      }))
      .filter((x) => x.url && isValidHttpUrl(x.url))
      .slice(0, MAX_LINKS);

    const serviceCatWhitelist = new Set(SERVICE_CATEGORIES.map((x) => x.value));
    const safeServiceCategories = (Array.isArray(serviceCategories)
      ? serviceCategories
      : []
    )
      .map((x) => String(x || "").trim())
      .filter((x) => x && serviceCatWhitelist.has(x))
      .slice(0, MAX_SERVICE_CATEGORIES);

    const safeServiceTags = (Array.isArray(serviceTags) ? serviceTags : [])
      .map((x) => normalizeTag(x))
      .filter((x) => x && isValidTag(x))
      .slice(0, MAX_SERVICE_TAGS);

    try {
      const response = await fetch(
  `${API_BASE}/api/account/${encodeURIComponent(userId)}`,
  {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      username,
      avatar: avatarUrl,
      bio,
      gender,
      profileType: safeProfileType,
      birthDate,
      location,
      showLocation,
      phone,
      links: safeLinks,

      serviceCategories: safeServiceCategories,
      serviceTags: safeServiceTags,
    }),
  }
);

        if (response.status === 409) {
        let reason =
          "Конфлікт або застарілі дані. Оновіть сторінку й спробуйте ще раз.";
        try {
          const d = await response.json();
          const msg = String(d?.error || "").toLowerCase();
          if (
            msg.includes("username") ||
            msg.includes("handle") ||
            msg.includes("exists") ||
            msg.includes("ім’я користувача") ||
            msg.includes("вже існує") ||
            msg.includes("вже зайнято")
          ) {
            reason = "Це ім’я користувача вже зайнято. Оберіть інше.";
          }
        } catch {}
        setError(reason);
        setIsUploading(false);
        return false;
      }

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Помилка при збереженні профілю");

      if (typeof data?.user?.profileCompletion === "number") {
        setProfileCompletion(data.user.profileCompletion);
      }

      setAvatar(null);
      setShouldDeleteAvatar(false);
      setPreviousAvatarUrl(avatarUrl);
      setAvatarPreview(avatarUrl);

      const newSnap = buildSnapshot({
        username,
        bio,
        gender,
        profileType: safeProfileType,
        birthDate,
        location,
        phone,
        showLocation,

        avatarUrl,
        previousAvatarUrl: avatarUrl,
        shouldDeleteAvatar: false,
        hasNewAvatarFile: false,

        links: safeLinks,
        serviceCategories: safeServiceCategories,
        serviceTags: safeServiceTags,
      });
      setInitialSnapshot(newSnap);

      setSuccessMessage("Профіль оновлено успішно");
      setIsUploading(false);
      return true;
    } catch (err) {
      console.error(err);
      setError(err?.message || "Помилка при збереженні профілю");
      setIsUploading(false);
      return false;
    }
  };

  const saveThenLeave = async () => {
    const ok = await saveProfile();
    if (!ok) return;

    setShowUnsavedAlert(false);

    if (pendingHrefRef.current) {
      const to = pendingHrefRef.current;
      clearLeaveContext();
      navigate(to);
      return;
    }

    if (pendingHistoryBackRef.current) {
      clearLeaveContext();
      allowPopRef.current = true;
      navHistory.back();
      return;
    }

    if (typeof pendingLeaveRef.current === "function") {
      const fn = pendingLeaveRef.current;
      clearLeaveContext();
      fn();
      return;
    }

    clearLeaveContext();
    navigate("/profile");
  };

    const handleSaveChanges = async () => {
    if (!isDirty) {
      setSuccessMessage("");
      setError("Ви ще не внесли жодних змін");
      return;
    }

    const ok = await saveProfile();
    if (!ok) return;
    setTimeout(() => navigate("/profile"), 650);
  };

  const ring = useMemo(() => {
    const size = isMobile ? 102 : 118;
    const stroke = 6;
    const radius = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const progress = Math.max(0, Math.min(100, profileCompletion));
    const offset = circumference - (progress / 100) * circumference;
    return { size, stroke, radius, circumference, offset, progress };
  }, [profileCompletion, isMobile]);

  const remainingLinks = Math.max(0, MAX_LINKS - links.length);
  const remainingServiceCats = Math.max(
    0,
    MAX_SERVICE_CATEGORIES - serviceCategories.length
  );
  const remainingServiceTags = Math.max(0, MAX_SERVICE_TAGS - serviceTags.length);

  return (
    <div className="edit-profile">
      <DynamicHeader
        title="Редагувати профіль"
        variant="solid"
        size="regular"
        hideOnScroll={true}
        showBack={true}
        onBack={() => attemptLeave(() => navigate(-1))}
        withSpacer={true}
      />

      <Alerts
        open={showUnsavedAlert}
        variant="unsavedChanges"
        onClose={closeUnsavedAlert}
        onCancel={closeUnsavedAlert}
        onSecondary={proceedWithoutSaving}
        onPrimary={saveThenLeave}
      />

      <div className="edit-profile__content">
        <div className="edit-profile__avatar-wrapper">
          <div className="edit-profile__ring">
            <svg
              className="edit-profile__ring-svg"
              width={ring.size}
              height={ring.size}
              viewBox={`0 0 ${ring.size} ${ring.size}`}
            >
              <circle
                className="edit-profile__ring-bg"
                cx={ring.size / 2}
                cy={ring.size / 2}
                r={ring.radius}
                strokeWidth={ring.stroke}
                fill="none"
              />
              <circle
                className="edit-profile__ring-fg"
                cx={ring.size / 2}
                cy={ring.size / 2}
                r={ring.radius}
                strokeWidth={ring.stroke}
                fill="none"
                strokeDasharray={ring.circumference}
                strokeDashoffset={ring.offset}
              />
            </svg>

            <div className="edit-profile__avatar" onClick={handleAvatarClick}>
              {avatarPreview ? (
                <img
                  src={avatarPreview}
                  alt="User Avatar"
                  className="edit-profile__avatar-img"
                />
              ) : (
                <div className="edit-profile__avatar-placeholder">Додати аватар</div>
              )}

              <div className="edit-profile__ring-label" title="Заповнено профілю">
                {ring.progress}%
              </div>
            </div>
          </div>

          <input
            type="file"
            id="avatar-upload"
            accept="image/*"
            onChange={handleAvatarChange}
            style={{ display: "none" }}
          />

          {isAvatarMenuOpen && (
            <div
              ref={avatarMenuRef}
              className={
                "edit-profile__avatar-menu" +
                (isAvatarMenuClosing ? " is-closing" : " is-open")
              }
            >
              <label htmlFor="avatar-upload" className="edit-profile__avatar-menu-item">
                Завантажити нову
              </label>

              {avatarPreview !== DEFAULT_AVATAR_URL && (
                <button
                  className="edit-profile__avatar-menu-item danger"
                  onClick={handleDeleteAvatar}
                >
                  Видалити аватарку
                </button>
              )}
            </div>
          )}
        </div>

        {isAvatarCropOpen && (
          <div className="ep-cropper">
            <div
              className="ep-cropper__backdrop"
              onClick={() => {
                if (!isCropping) closeAvatarCrop();
              }}
            />

            <div
              className="ep-cropper__card"
              role="dialog"
              aria-modal="true"
              aria-label="Обрізка аватарки"
            >
              <div className="ep-cropper__top">
                <button
                  type="button"
                  className="ep-cropper__btn ep-cropper__btn--ghost"
                  onClick={() => closeAvatarCrop()}
                  disabled={isCropping}
                >
                  Скасувати
                </button>

                <div className="ep-cropper__title">Аватарка</div>

                <button
                  type="button"
                  className="ep-cropper__btn ep-cropper__btn--primary"
                  onClick={applyAvatarCrop}
                  disabled={isCropping}
                >
                  {isCropping ? "Готово..." : "Готово"}
                </button>
              </div>

              <div className="ep-cropper__area">
                {avatarCropSrc && (
                  <Cropper
                    image={avatarCropSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    cropShape="round"
                    showGrid={false}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                    zoomWithScroll={true}
                    restrictPosition={true}
                    objectFit="cover"
                  />
                )}
              </div>

              <div className="ep-cropper__controls">
                <div className="ep-cropper__zoom-row">
                  <span className="ep-cropper__zoom-label">Зум</span>
                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.01}
                    value={zoom}
                    onChange={(e) => setZoom(Number(e.target.value))}
                    className="ep-cropper__zoom"
                    disabled={isCropping}
                  />
                </div>

                <div className="ep-cropper__hint">
                  Перетягни фото, щоб вибрати область. Масштабуй повзунком або колесом миші.
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="edit-profile__info">
          <label htmlFor="username" className="edit-profile__label">
            Ім’я користувача
          </label>
          <input
            ref={usernameRef}
            type="text"
            id="username"
            value={username}
            onChange={handleUsernameChange}
            className={"edit-profile__input" + (fieldErrors.username ? " is-invalid" : "")}
          />
        </div>

        <div className="edit-profile__info">
          <label htmlFor="bio" className="edit-profile__label">
            Про себе
          </label>
          <textarea
            id="bio"
            value={bio}
            onChange={handleBioChange}
            maxLength={100}
            className="edit-profile__textarea"
            placeholder="Кілька слів про себе..."
          />
        </div>

        <div className="edit-profile__info">
          <label htmlFor="gender" className="edit-profile__label">
            Стать
          </label>
          <select
            id="gender"
            value={gender}
            onChange={handleGenderChange}
            className="edit-profile__select"
          >
            <option value="">Оберіть стать</option>
            <option value="male">Чоловік</option>
            <option value="female">Жінка</option>
          </select>
        </div>

        <div className="edit-profile__info">
          <label htmlFor="profileType" className="edit-profile__label">
            Тип акаунта
            <span style={{ display: "block", fontSize: 12, opacity: 0.7, marginTop: 4 }}>
              Впливатиме на рекомендації та персональну стрічку
            </span>
          </label>
          <select
            ref={profileTypeRef}
            id="profileType"
            value={profileType}
            onChange={handleProfileTypeChange}
            className={"edit-profile__select" + (fieldErrors.profileType ? " is-invalid" : "")}
          >
            <option value="">Оберіть тип</option>
            {PROFILE_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        {specRender && (
          <div
            className={
              "edit-profile__info ep-accordion" +
              (specExpanded ? " is-open" : " is-closed")
            }
          >
            <label className="edit-profile__label">
              Спеціалізація та теги (для запитів)
              <span style={{ display: "block", fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                Оберіть до {MAX_SERVICE_CATEGORIES} категорій та додайте теги. Це допоможе людям знаходити вас точніше.
              </span>
            </label>

            <div className="ep-accordion__inner">
              <div className="ep-cats">
                {SERVICE_CATEGORIES.map((c) => {
                  const active = serviceCategories.includes(c.value);
                  return (
                    <button
                      key={c.value}
                      type="button"
                      className={"ep-chip" + (active ? " active" : "")}
                      onClick={() => toggleServiceCategory(c.value)}
                      aria-pressed={active}
                      title={c.label}
                    >
                      {c.label}
                    </button>
                  );
                })}
              </div>

              <div className="edit-profile__subnote">
                Залишилось {remainingServiceCats} з {MAX_SERVICE_CATEGORIES} категорій.
              </div>

              <div className="ep-tags">
                <div className="ep-tags__row">
                  <input
                    type="text"
                    className="edit-profile__input"
                    placeholder="Додати тег (наприклад: манікюр, полірування, дітейлінг...)"
                    value={serviceTagDraft}
                    onChange={(e) => setServiceTagDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") addServiceTag();
                    }}
                  />
                  <button
                    type="button"
                    className="ep-tags__add"
                    onClick={addServiceTag}
                    disabled={serviceTags.length >= MAX_SERVICE_TAGS}
                    title={serviceTags.length >= MAX_SERVICE_TAGS ? `Ліміт: ${MAX_SERVICE_TAGS}` : "Додати"}
                  >
                    Додати
                  </button>
                </div>

                {serviceTags.length > 0 && (
                  <div className="ep-tags__list">
                    {serviceTags.map((t, idx) => (
                      <div key={idx} className="ep-tags__item">
                        <span className="ep-tags__text">{t}</span>
                        <button
                          type="button"
                          className="ep-tags__remove"
                          onClick={() => removeServiceTagAt(idx)}
                          aria-label="Видалити"
                          title="Видалити"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="edit-profile__subnote">
                  Залишилось {remainingServiceTags} з {MAX_SERVICE_TAGS} тегів.
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="edit-profile__info">
          <label htmlFor="birthDate" className="edit-profile__label">
            Дата народження
          </label>
          <input
            type="date"
            id="birthDate"
            value={birthDate}
            onChange={(e) => setBirthDate(e.target.value)}
            className="edit-profile__input"
          />
        </div>

        <div className="edit-profile__info">
          <label htmlFor="location" className="edit-profile__label">
            Місто
          </label>
          <input
            type="text"
            id="location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            className="edit-profile__input"
            placeholder="Наприклад, Київ"
          />
          <div className="edit-profile__checkbox">
            <input
              type="checkbox"
              id="showLocation"
              checked={showLocation}
              onChange={(e) => setShowLocation(e.target.checked)}
            />
            <label htmlFor="showLocation">Показувати місто в профілі</label>
          </div>
        </div>

        <div className="edit-profile__info">
          <label htmlFor="phone" className="edit-profile__label">
            Номер телефону
            <span style={{ display: "block", fontSize: 12, opacity: 0.7, marginTop: 4 }}>
              Номер телефону не видно нікому, його бачите тільки ви. Видимість номеру ви можете налаштувати самі під час створення оголошення.
            </span>
          </label>
          <input
            ref={phoneRef}
            type="tel"
            id="phone"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value.replace(/[^\d+]/g, ""));
              setFieldErrors((prev) => ({ ...prev, phone: false }));
            }}
            className={"edit-profile__input" + (fieldErrors.phone ? " is-invalid" : "")}
            placeholder="Наприклад: +380681234567 або 0681234567"
          />
        </div>

        <div className="edit-profile__info">
          <label className="edit-profile__label">
            Активні посилання (опційно)
            <span style={{ display: "block", fontSize: 12, opacity: 0.7, marginTop: 4 }}>
              Залишилось {remainingLinks} з {MAX_LINKS}
            </span>
          </label>

          <input
            type="text"
            className="edit-profile__input"
            placeholder="Мітка (Instagram, Сайт, Telegram...)"
            value={linkLabel}
            onChange={(e) => setLinkLabel(e.target.value)}
          />

          <div className="links-simple__row">
            <input
              type="text"
              className="edit-profile__input"
              placeholder="Посилання (наприклад, instagram.com/username)"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") addLink();
              }}
            />
            <button
              type="button"
              className="links-simple__add"
              onClick={addLink}
              disabled={links.length >= MAX_LINKS}
              title={links.length >= MAX_LINKS ? "Ліміт: 3" : "Додати"}
            >
              Додати
            </button>
          </div>

          {links.length > 0 && (
            <ul className="links-simple__list">
              {links.map((l, idx) => (
                <li key={idx} className="links-simple__item">
                  <div className="links-simple__texts">
                    <span className="links-simple__label">{l.label || l.url}</span>
                    <span className="links-simple__url">{l.url}</span>
                  </div>
                  <button
                    type="button"
                    className="links-simple__remove"
                    onClick={() => removeLinkAt(idx)}
                    aria-label="Видалити"
                    title="Видалити"
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="links-simple__hint">
            Підтримуються будь-які URL (сайти, соцмережі). Приклад:{" "}
            <code>instagram.com/username</code>
          </div>
        </div>

         <div className="edit-profile__actions">
          <button
            className="edit-profile__save-btn"
            onClick={handleSaveChanges}
            disabled={isUploading}
            aria-disabled={isUploading}
          >
            {isUploading ? "Завантаження..." : "Зберегти зміни"}
          </button>
        </div>

        {error && <p className="edit-profile__error">{error}</p>}
        {successMessage && <p className="edit-profile__success">{successMessage}</p>}
      </div>
    </div>
  );
};

export default EditProfile;